import React, { useEffect, useState, type JSX } from 'react';
import FormControl from '@mui/material/FormControl';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import DeleteIcon from '@mui/icons-material/Delete';
import { SPACING_BETWEEN_FORM_FIELDS } from './common/spacings';
import { ACHSENTYPEN } from './Bereifung';
import { Achse as AchseTyp, Fahrzeugtyp } from '../types';
import { FloatFormat } from './common/inputFormats';
import { BereifungsartDAT, SICHERHEITSSYSTEME, GESCHWINDIGKEITSINDEX, REIFENMARKEN_BY_HERSTELLER } from '../shared/constants';

type Props = {
  readonly achse: AchseTyp;
  readonly index: number;
  readonly achseLoeschen: (position: string) => void;
  readonly achsenbereifungAendern: (achsenPosition: string, typ: string) => void;
  readonly fahrzeugtyp: Fahrzeugtyp;
  readonly mehrAlsZweiAchsen: boolean;
  readonly profiltiefeAendern: (achsenposition: string, position: string, profiltiefe: string) => void;
  readonly reifengroesseAendern: (achsenposition: string, reifengroesse: string) => void;
  readonly herstellerAendern: (achsenposition: string, hersteller: string) => void;
  readonly isDisabled: boolean;
  readonly reifenHersteller: string[];
  readonly tragfaehigkeitsindexAendern: (achsenposition: string, tragfaehigkeitsindex: string) => void;
  readonly tragfaehigkeitsindex2Aendern: (achsenposition: string, tragfaehigkeitsindex2: string) => void;
  readonly geschwindigkeitsindexAendern: (achsenposition: string, geschwindigkeitsindex: string) => void;
  readonly sicherheitssystemAendern: (achsenposition: string, sicherheitssystem: string) => void;
  readonly bereifungsartAendern: (achsenposition: string, bereifungsart: string) => void;
  readonly reifenmarkeAendern: (achsenposition: string, reifenmarke: string) => void;
};

export function Achse({
  achse,
  index,
  achseLoeschen,
  achsenbereifungAendern,
  fahrzeugtyp,
  mehrAlsZweiAchsen,
  profiltiefeAendern,
  reifengroesseAendern,
  herstellerAendern,
  isDisabled,
  reifenHersteller,
  tragfaehigkeitsindexAendern,
  tragfaehigkeitsindex2Aendern,
  geschwindigkeitsindexAendern,
  sicherheitssystemAendern,
  bereifungsartAendern,
  reifenmarkeAendern
}: Props): JSX.Element {
  const filterOptions = createFilterOptions({ matchFrom: 'start', trim: true });

  const [reifengroesse, setReifengroesse] = useState<string>(achse.reifengroesse ?? '');
  const [tragfaehigkeitsindex, setTragfaehigkeitsindex] = useState<string>(achse.tragfaehigkeitsindex ?? '');
  const [tragfaehigkeitsindex2, setTragfaehigkeitsindex2] = useState<string>(achse.tragfaehigkeitsindex2 ?? '');

  const reifen = achse.reifen ?? [];

  let achseLabel = '';
  if (mehrAlsZweiAchsen) {
    achseLabel = `${index + 1}. Achse`;
  } else {
    achseLabel = index === 1 ? 'Hinten' : 'Vorn';
  }

  useEffect(() => {
    setReifengroesse(achse.reifengroesse ?? '');
  }, [achse.reifengroesse]);

  useEffect(() => {
    setTragfaehigkeitsindex(achse.tragfaehigkeitsindex ?? '');
  }, [achse.tragfaehigkeitsindex]);

  useEffect(() => {
    setTragfaehigkeitsindex2(achse.tragfaehigkeitsindex2 ?? '');
  }, [achse.tragfaehigkeitsindex2]);

  return (
    <Grid
      container
      spacing={SPACING_BETWEEN_FORM_FIELDS}
      sx={{
        paddingBottom: '1rem'
      }}
    >
      <Grid size={{ xs: 12, sm: 1 }}>
        <Typography
          variant="subtitle1"
          sx={(theme) => ({
            color: theme.palette.text.secondary
          })}
        >
          {achseLabel}
        </Typography>
      </Grid>
      {fahrzeugtyp !== Fahrzeugtyp.ZWEIRAEDRIG && (
        <Grid size={{ xs: 12, sm: 3 }}>
          <FormControl variant="standard" fullWidth>
            <InputLabel>Achsenbereifung</InputLabel>
            <Select
              variant="standard"
              label="Achsenbereifung"
              value={achse.typ}
              disabled={isDisabled}
              onChange={(event: SelectChangeEvent<string>) => {
                achsenbereifungAendern(achse.achsenPosition, event.target.value);
              }}
              data-testid={`fahrzeugdaten-achsenbereifung-${index}`}
            >
              {ACHSENTYPEN.map((g) => (
                <MenuItem key={g.value} value={g.value} data-testid={`${g.label}-${index}`}>
                  {g.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      )}
      <Grid size={{ xs: 12, sm: 2 }}>
        <Autocomplete
          disabled={isDisabled}
          filterOptions={filterOptions}
          freeSolo
          options={reifenHersteller}
          value={achse.hersteller ?? ''}
          onBlur={(event: React.FocusEvent<HTMLInputElement>) => {
            if (event.target.value !== achse.hersteller) {
              herstellerAendern(achse.achsenPosition, event.target.value);
            }
          }}
          renderInput={(params) => (
            <TextField variant="standard" {...params} label="Hersteller" disabled={isDisabled} fullWidth data-testid={`fahrzeugdaten-hersteller-${index}`} />
          )}
        />
      </Grid>
      <Grid size={{ xs: 12, sm: 2 }}>
        <TextField
          variant="standard"
          label="Reifengröße"
          value={reifengroesse}
          disabled={isDisabled}
          onChange={(event) => setReifengroesse(event.target.value)}
          onBlur={(event) => {
            if (event.target.value !== (achse.reifengroesse ?? '')) {
              reifengroesseAendern(achse.achsenPosition, event.target.value);
            }
          }}
          fullWidth
          data-testid={`fahrzeugdaten-reifengroesse-${index}`}
        />
      </Grid>
      <Grid size={{ xs: 12, sm: 1 }}>
        <TextField
          variant="standard"
          label="Tragfähigkeitsindex"
          value={tragfaehigkeitsindex}
          disabled={isDisabled}
          onChange={(event) => setTragfaehigkeitsindex(event.target.value)}
          onBlur={(event) => {
            if (event.target.value !== (achse.tragfaehigkeitsindex ?? '')) {
              tragfaehigkeitsindexAendern(achse.achsenPosition, event.target.value);
            }
          }}
          fullWidth
          data-testid={`fahrzeugdaten-tragfaehigkeitsindex-${index}`}
          inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
        />
      </Grid>
      <Grid size={{ xs: 12, sm: 1 }}>
        <TextField
          variant="standard"
          label="Tragfähigkeitsindex 2"
          value={tragfaehigkeitsindex2}
          disabled={isDisabled}
          onChange={(event) => setTragfaehigkeitsindex2(event.target.value)}
          onBlur={(event) => {
            if (event.target.value !== (achse.tragfaehigkeitsindex2 ?? '')) {
              tragfaehigkeitsindex2Aendern(achse.achsenPosition, event.target.value);
            }
          }}
          fullWidth
          data-testid={`fahrzeugdaten-tragfaehigkeitsindex2-${index}`}
          inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
        />
      </Grid>
      <Grid size={{ xs: 12, sm: 1 }}>
        <Autocomplete
          disabled={isDisabled}
          filterOptions={filterOptions}
          freeSolo
          options={GESCHWINDIGKEITSINDEX}
          value={achse.geschwindigkeitsindex ?? ''}
          onBlur={(event: React.FocusEvent<HTMLInputElement>) => {
            if (event.target.value !== achse.geschwindigkeitsindex) {
              geschwindigkeitsindexAendern(achse.achsenPosition, event.target.value);
            }
          }}
          renderInput={(params) => (
            <TextField
              variant="standard"
              {...params}
              label="Geschwindigkeitsindex"
              disabled={isDisabled}
              fullWidth
              data-testid={`fahrzeugdaten-geschwindigkeitsindex-${index}`}
            />
          )}
        />
      </Grid>
      <Grid size={{ xs: 12, sm: 2 }}>
        <Autocomplete
          disabled={isDisabled}
          filterOptions={filterOptions}
          freeSolo
          options={SICHERHEITSSYSTEME}
          value={achse.sicherheitssystem ?? ''}
          onBlur={(event: React.FocusEvent<HTMLInputElement>) => {
            if (event.target.value !== achse.sicherheitssystem) {
              sicherheitssystemAendern(achse.achsenPosition, event.target.value);
            }
          }}
          renderInput={(params) => (
            <TextField
              variant="standard"
              {...params}
              label="Sicherheitssystem"
              disabled={isDisabled}
              fullWidth
              data-testid={`fahrzeugdaten-sicherheitssystem-${index}`}
            />
          )}
        />
      </Grid>
      <Grid size={{ xs: 12, sm: 2 }}>
        <FormControl variant="standard" fullWidth>
          <InputLabel>Bereifungsart</InputLabel>
          <Select
            variant="standard"
            label="Bereifungsart"
            value={achse.bereifungsart ?? BereifungsartDAT.SOMMER}
            disabled={isDisabled}
            onChange={(event: SelectChangeEvent<string>) => {
              bereifungsartAendern(achse.achsenPosition, event.target.value);
            }}
            data-testid={`fahrzeugdaten-bereifungsart-${index}`}
          >
            <MenuItem value={BereifungsartDAT.SOMMER} data-testid={`sommer-${index}`}>
              Sommerreifen
            </MenuItem>
            <MenuItem value={BereifungsartDAT.WINTER} data-testid={`winter-${index}`}>
              Winterreifen
            </MenuItem>
            <MenuItem value={BereifungsartDAT.ALLWETTER} data-testid={`allwetter-${index}`}>
              Ganzjahresreifen
            </MenuItem>
            <MenuItem value={BereifungsartDAT.ALL_TERRAIN} data-testid={`all-terrain-${index}`}>
              All-Terrain
            </MenuItem>
            <MenuItem value={BereifungsartDAT.MUD_TERRAIN} data-testid={`mud-terrain-${index}`}>
              Mud-Terrain
            </MenuItem>
          </Select>
        </FormControl>
      </Grid>
      <Grid size={{ xs: 12, sm: 2 }}>
        <Autocomplete
          disabled={isDisabled}
          filterOptions={filterOptions}
          freeSolo
          options={achse.hersteller ? REIFENMARKEN_BY_HERSTELLER[achse.hersteller] || [] : []}
          value={achse.reifenmarke ?? ''}
          onBlur={(event: React.FocusEvent<HTMLInputElement>) => {
            if (event.target.value !== achse.reifenmarke) {
              reifenmarkeAendern(achse.achsenPosition, event.target.value);
            }
          }}
          renderInput={(params) => (
            <TextField variant="standard" {...params} label="Reifenmarke" disabled={isDisabled} fullWidth data-testid={`fahrzeugdaten-reifenmarke-${index}`} />
          )}
        />
      </Grid>
      {reifen?.map((einzelReifen, i) => (
        <Grid size={{ xs: 12, sm: 1 }} key={i}>
          <Tooltip
            title={
              profiltiefeUngueltig(einzelReifen?.profiltiefe ?? '')
                ? 'Das Hauptprofil muss am ganzen Umfang eine Profiltiefe von mindestens 1,6 Millimeter aufweisen; als Hauptprofil gelten dabei die breiten Profilrillen im mittleren Bereich der Lauffläche, der etwa 3/4 der Laufflächenbreite einnimmt. Jedoch genügt bei Fahrrädern mit Hilfsmotor, Kleinkrafträdern und Leichtkrafträdern eine Profiltiefe von mindestens 1 mm.'
                : ''
            }
          >
            <div>
              <TextField
                variant="standard"
                label={einzelReifen.position}
                error={profiltiefeUngueltig(einzelReifen?.profiltiefe ?? '')}
                value={einzelReifen.profiltiefe ?? 0}
                disabled={isDisabled}
                onChange={(event) => {
                  if (event.target.value !== einzelReifen.profiltiefe) {
                    profiltiefeAendern(achse.achsenPosition, einzelReifen.position, event.target.value);
                  }
                }}
                fullWidth
                data-testid={`fahrzeugdaten-profiltiefe-${index}-${i}-${einzelReifen.position}`}
                InputProps={{
                  inputComponent: FloatFormat
                }}
              />
            </div>
          </Tooltip>
        </Grid>
      ))}
      {index > 1 && (
        <Grid size={12} sx={{ textAlign: 'right' }} {...((achse.reifen || []).length === 2 ? { sm: 2 } : { sm: 4 })}>
          <IconButton
            onClick={() => {
              achseLoeschen(achse.achsenPosition);
            }}
            disabled={isDisabled}
            size="large"
          >
            <DeleteIcon />
          </IconButton>
        </Grid>
      )}
    </Grid>
  );
}

export function profiltiefeUngueltig(profiltiefe: string): boolean {
  if (!profiltiefe) {
    return false;
  }

  const value = profiltiefe.replace(',', '.');

  /*
   * Einschränkung erfolgte vor der Umstellung auf v5 in react-number-format über die Prop decimalScale={1}.
   * Da jetzt das Format als InputProp an das Textfield übergeben wird, wurde es vorübergehend hier zu einer Validierung.
   */
  if ((value.split('.')[1] || '0').length > 1) {
    return true;
  }

  const p = Number.parseFloat(value);
  return (p !== 0 && p < 1.6) || p > 22;
}
